/**
 * Splash → Welcome back → Site picker.
 *
 * Wraps the routed app. When the user lands on the app for the first time
 * in a fresh session AND they're logged in AND have at least one site,
 * we play this three-step intro before handing over to the routes.
 *
 *  1. Big logo on brand-green background (1.5s)
 *  2. "Welcome back, [name]" (1.0s)
 *  3. "Which site are you working on today?" — site picker
 *
 * If the user only has one site, the picker is auto-skipped.
 *
 * The "shown this session" flag lives in sessionStorage so the splash plays
 * once per app open, not every route change. Closing the PWA / browser tab
 * clears sessionStorage so it'll show again next time they open the app.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useSession } from '../lib/auth';
import { useUserOrg } from '../lib/org';
import { useCurrentSite } from '../lib/currentSite';
import { listSites, type Site } from '../lib/db';
import { Logo } from './Logo';

type Phase = 'splash' | 'welcome' | 'sitepicker' | 'done';

interface Props { children: ReactNode }

export function SplashGate({ children }: Props) {
  const { session, loading: sessionLoading } = useSession();
  const { org } = useUserOrg();
  const { setCurrentSite } = useCurrentSite();

  const [phase, setPhase] = useState<Phase>('splash');

  const [sites, setSites] = useState<Site[] | null>(null);

  // Wait for auth to load before deciding what to do after the splash.
  // The splash phase ALWAYS plays on cold open (logged-in or not) so the
  // brand is the first thing the user sees on the PWA, just like on web.

  // Pre-load sites in the background so the picker is ready when we get there
  useEffect(() => {
    if (phase === 'done' || !org) return;
    let cancelled = false;
    listSites(org.org_id).then((s) => {
      if (!cancelled) setSites(s);
    }).catch(() => { if (!cancelled) setSites([]); });
    return () => { cancelled = true; };
  }, [phase, org]);

  // Phase 1 → 2 timer (don't advance until auth is done loading so
  // the welcome screen has the right session info to render with)
  useEffect(() => {
    if (phase !== 'splash') return;
    if (sessionLoading) return;
    const t = setTimeout(() => setPhase('welcome'), 1500);
    return () => clearTimeout(t);
  }, [phase, sessionLoading]);

  // Phase 2 → done. The home page is itself a site picker now, so we don't
  // need a picker phase inside the splash.
  useEffect(() => {
    if (phase !== 'welcome') return;
    const t = setTimeout(() => {
      if (sites && sites.length === 1) {
        setCurrentSite(sites[0].id);   // single-site users get an auto-pick
      }
      finish();
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, sites, session]);

  function finish() {
    setPhase('done');
  }

  function pickSite(s: Site) {
    setCurrentSite(s.id);
    finish();
  }

  function skipPicker() {
    finish();
  }

  // Once we're done, hand off to the routed app.
  // Note: we DO play splash for not-signed-in users too (cold sign-up flow)
  // — the brand should be the first thing every visitor sees. The welcome
  // phase below renders a "Let's get you signed in" variant when no session
  // is present, then finish() drops them into the login route.
  if (phase === 'done') return <>{children}</>;

  // ---------- Splash ----------
  if (phase === 'splash') {
    return (
      <div className="splash-screen splash-fade-in">
        <div className="splash-stack">
          <div className="splash-logo"><Logo size={140} decorative /></div>
          <h1 className="splash-wordmark">Safe and Sorted</h1>
          <p className="splash-tagline">Health &amp; safety, built for builders</p>
        </div>
      </div>
    );
  }

  // ---------- Welcome back ----------
  if (phase === 'welcome') {
    if (!session) {
      // Not signed in — splash → straight to login. Don't show 'welcome back'.
      // (The post-welcome timer below short-circuits to finish() when no session.)
      return (
        <div className="splash-screen splash-fade-in">
          <div className="splash-stack">
            <h1 className="splash-welcome">Let's get you signed in</h1>
          </div>
        </div>
      );
    }
    const userName =
      (session.user.user_metadata?.full_name as string | undefined)
      || session.user.email?.split('@')[0]
      || '';
    return (
      <div className="splash-screen splash-fade-in">
        <div className="splash-stack">
          <h1 className="splash-welcome">
            Welcome back{userName ? `, ${userName.split(' ')[0]}` : ''}
          </h1>
        </div>
      </div>
    );
  }

  // ---------- Site picker ----------
  return (
    <div className="splash-screen splash-screen-light splash-fade-in">
      <div className="splash-picker">
        <h1 className="splash-picker-title">Which site are you working on today?</h1>
        <p className="splash-picker-sub">Tap one to set it as active for the day.</p>
        <ul className="splash-picker-list">
          {(sites ?? []).map((s) => (
            <li key={s.id}>
              <button type="button" className="splash-picker-row" onClick={() => pickSite(s)}>
                <span className="splash-picker-name">{s.name}</span>
                {s.address && <span className="splash-picker-meta">{s.address}</span>}
                <span className="splash-picker-cta">Open →</span>
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="splash-picker-skip" onClick={skipPicker}>
          Skip — let me choose later
        </button>
      </div>
    </div>
  );
}
